/**
 * Auth token storage backed by in-memory Redux session slices (admin vs user).
 * Tokens are mirrored to tab-scoped sessionStorage so a refresh can restore the session.
 * They are never written to localStorage or redux-persist (HIPAA: no durable token/PHI at rest).
 */

import { adminPaths, userPaths } from 'routes/constants';
import {
  clearAdminSession,
  setAdminSessionTokens,
  type AdminSessionState,
} from 'store/slices/adminSessionSlice';
import {
  clearUserSession,
  setUserSessionTokens,
  type UserSessionState,
} from 'store/slices/userSessionSlice';
import { store, type RootState } from 'store/store';

import type { AuthResult, ChallengeSession } from 'services/auth/types';

import { clearPostLoginRedirect } from './postLoginRedirectStorage';
import { clearSessionRealm, getSessionRealm, type SessionRealm } from './sessionRealm';
import { clearTabSessionTokens, persistTabSessionTokens } from './sessionTokenTabStorage';

const ORG_ID_COOKIE = 'spence_org_id';

/** Legacy storage keys removed on startup and logout. */
const LEGACY_TOKEN_LOCAL_STORAGE_KEYS = [
  'idToken',
  'refreshToken',
  'idToken_admin',
  'refreshToken_admin',
] as const;

/** @deprecated Legacy key — only used when purging old builds. */
export const REFRESH_TOKEN_LOCAL_STORAGE_KEY = 'refreshToken';

const LEGACY_TOKEN_COOKIE_NAMES = [
  'spence_access_token',
  'spence_id_token',
  'spence_refresh_token',
  'spence_admin_access_token',
  'spence_admin_id_token',
  'spence_admin_refresh_token',
  'spence_user_access_token',
  'spence_user_id_token',
  'spence_user_refresh_token',
] as const;

function setCookie(name: string, value: string, days: number): void {
  if (typeof document === 'undefined') return;
  const encoded = encodeURIComponent(value);
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  const forcedProtocol = (globalThis as { __cookieProtocol?: string }).__cookieProtocol;
  const protocol =
    forcedProtocol ?? (typeof window !== 'undefined' ? window.location?.protocol : '');
  const isHttps = protocol === 'https:';
  document.cookie = `${name}=${encoded}; expires=${expires}; path=/; SameSite=Lax${
    isHttps ? '; Secure' : ''
  }`;
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const cookie of cookies) {
    const [key, ...rest] = cookie.split('=');
    if (key === name) {
      return decodeURIComponent(rest.join('='));
    }
  }

  return null;
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return;
  const forcedProtocol = (globalThis as { __cookieProtocol?: string }).__cookieProtocol;
  const protocol =
    forcedProtocol ?? (typeof window !== 'undefined' ? window.location?.protocol : '');
  const isHttps = protocol === 'https:';
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax${
    isHttps ? '; Secure' : ''
  }`;
}

const CHALLENGE_SESSION_KEY = 'spence_challenge_session';

let challengeSession: ChallengeSession | null = null;

function resolveAuthRealm(explicitRealm?: SessionRealm): SessionRealm {
  return explicitRealm ?? getSessionRealm() ?? 'admin';
}

function getActiveRealm(): SessionRealm | null {
  return getSessionRealm();
}

function readAdminSession(state: RootState): AdminSessionState {
  return state.adminSession;
}

function readUserSession(state: RootState): UserSessionState {
  return state.userSession;
}

function sessionHasTokens(session: AdminSessionState | UserSessionState): boolean {
  return !!(session.accessToken?.trim() || session.idToken?.trim());
}

/** Removes legacy token keys from localStorage (older builds). */
export function purgeLegacyTokenStorage(): void {
  if (typeof window === 'undefined' || typeof window.localStorage?.removeItem !== 'function') {
    return;
  }

  for (const key of LEGACY_TOKEN_LOCAL_STORAGE_KEYS) {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // Ignore quota / privacy mode.
    }
  }
}

function purgeLegacyTokenCookies(): void {
  for (const name of LEGACY_TOKEN_COOKIE_NAMES) {
    deleteCookie(name);
  }
}

/**
 * Drop any existing in-memory / tab session so a new login can replace it.
 * Does not redirect (unlike `terminateSession`) and does not clear an in-flight
 * MFA / EU Cognito challenge — callers set those after this when needed.
 */
export function clearPriorAuthSession(): void {
  store.dispatch(clearAdminSession());
  store.dispatch(clearUserSession());
  clearTabSessionTokens();
  purgeLegacyTokenStorage();
  purgeLegacyTokenCookies();
  deleteCookie(ORG_ID_COOKIE);
  clearSessionRealm();
}

/**
 * Store auth result for a session realm. Only one realm stays active: writing
 * tokens clears the other realm so a new login replaces the previous session.
 */
export function setAuthTokens(result: AuthResult, realm?: SessionRealm): void {
  const targetRealm = resolveAuthRealm(realm);
  const payload = {
    idToken: result.idToken ?? null,
    accessToken: result.accessToken ?? null,
    refreshToken: result.refreshToken ?? null,
  };

  if (targetRealm === 'admin') {
    store.dispatch(clearUserSession());
    clearTabSessionTokens('user');
    store.dispatch(setAdminSessionTokens(payload));
    persistTabSessionTokens('admin', payload);
    return;
  }

  store.dispatch(clearAdminSession());
  clearTabSessionTokens('admin');
  store.dispatch(setUserSessionTokens(payload));
  persistTabSessionTokens('user', payload);
}

export function getUserIdToken(): string | null {
  return readUserSession(store.getState()).idToken?.trim() || null;
}

export function getUserAccessToken(): string | null {
  return readUserSession(store.getState()).accessToken?.trim() || null;
}

export function getUserRefreshToken(): string | null {
  return readUserSession(store.getState()).refreshToken?.trim() || null;
}

export function getAccessToken(): string | null {
  const realm = getActiveRealm();
  const state = store.getState();
  const adminSession = readAdminSession(state);
  const userSession = readUserSession(state);

  let session: AdminSessionState | UserSessionState;
  if (realm === 'admin') {
    session = adminSession;
  } else if (realm === 'user') {
    session = userSession;
  } else if (adminSession.accessToken) {
    session = adminSession;
  } else {
    session = userSession;
  }

  return session.accessToken?.trim() || session.idToken?.trim() || null;
}

export function getIdToken(): string | null {
  const realm = getActiveRealm();
  // Prefer idToken; fall back to accessToken so API callers do not treat a
  // valid access-only session (common after EU Cognito) as logged out.
  if (realm === 'admin') {
    return getAdminIdToken() ?? getAdminAccessToken();
  }

  if (realm === 'user') {
    return getUserIdToken() ?? getUserAccessToken();
  }

  return getAdminIdToken() ?? getAdminAccessToken() ?? getUserIdToken() ?? getUserAccessToken();
}

export function getPreferredIdToken(): string | null {
  return getUserIdToken();
}

export function getAdminIdToken(): string | null {
  return readAdminSession(store.getState()).idToken?.trim() || null;
}

export function getAdminAccessToken(): string | null {
  return readAdminSession(store.getState()).accessToken?.trim() || null;
}

export function getRefreshToken(): string | null {
  const realm = getActiveRealm() ?? 'user';
  if (realm === 'admin') {
    return readAdminSession(store.getState()).refreshToken?.trim() || null;
  }

  return getUserRefreshToken();
}

/** Clears all stored auth tokens / session realm (same purge as `clearPriorAuthSession`). */
export function clearAuthTokens(): void {
  clearPriorAuthSession();
}

export function setOrgId(orgId: string): void {
  setCookie(ORG_ID_COOKIE, orgId, 7);
}

export function getOrgId(): string | null {
  return getCookie(ORG_ID_COOKIE);
}

export function clearOrgId(): void {
  deleteCookie(ORG_ID_COOKIE);
}

export function hasAuthTokens(): boolean {
  const realm = getActiveRealm();
  if (realm === 'admin') {
    return sessionHasTokens(readAdminSession(store.getState()));
  }

  if (realm === 'user') {
    return sessionHasTokens(readUserSession(store.getState()));
  }

  return (
    sessionHasTokens(readAdminSession(store.getState())) ||
    sessionHasTokens(readUserSession(store.getState()))
  );
}

export function setChallengeSession(session: ChallengeSession): void {
  challengeSession = session;
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(CHALLENGE_SESSION_KEY, JSON.stringify(session));
  }
}

export function getChallengeSession(): ChallengeSession | null {
  if (challengeSession) return challengeSession;
  if (typeof sessionStorage !== 'undefined') {
    const storedSession = sessionStorage.getItem(CHALLENGE_SESSION_KEY);
    if (storedSession) {
      try {
        challengeSession = JSON.parse(storedSession);
        return challengeSession;
      } catch {
        return null;
      }
    }
  }

  return null;
}

export function clearChallengeSession(): void {
  challengeSession = null;
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(CHALLENGE_SESSION_KEY);
  }
}

export function redirectToLogin(realm?: SessionRealm | null): void {
  // Admins always start fresh after session end (SCRUM-2096). Employees also
  // clear stored deep-links so the next login lands on Expenses via the
  // journey resolver — not the page that triggered expiry (e.g. PII).
  clearPostLoginRedirect();

  const targetPath = realm === 'admin' ? adminPaths.signIn : userPaths.signIn;
  window.location.assign(targetPath);
}
